import type { Ref } from 'vue'

import {
  listCitiesPassportIdmIdmPrivateListCitiesPost,
  listCountriesPassportIdmIdmPrivateListCountriesPost,
  listDepartmentsPassportIdmIdmPrivateListDepartmentsPost,
  listOfficesPassportIdmIdmPrivateListOfficesPost,
  listPositionsPassportIdmIdmPrivateListPositionsPost,
} from '@/shared/api/intranet'

export const useEmployeeOptions = () => {
  const { data: officesList, isLoading: isOfficesLoading } = useQuery({
    queryKey: ['officesList'],
    queryFn: () =>
      listOfficesPassportIdmIdmPrivateListOfficesPost({
        throwOnError: true,
        body: {
          filter: {},
        },
      }),
    select: (response) => response.data.offices?.sort((a, b) => a.name.localeCompare(b.name, 'ru')),
  })

  const { data: positionsList, isLoading: isPositionsLoading } = useQuery({
    queryKey: ['positionsList'],
    queryFn: () =>
      listPositionsPassportIdmIdmPrivateListPositionsPost({
        throwOnError: true,
        body: {
          filter: {},
        },
      }),
    select: (response) => response.data.positions?.sort((a, b) => a.name.localeCompare(b.name, 'ru')),
  })

  const { data: departmentsList, isLoading: isDepartmentsLoading } = useQuery({
    queryKey: ['departmentsList'],
    queryFn: () =>
      listDepartmentsPassportIdmIdmPrivateListDepartmentsPost({
        throwOnError: true,
        body: {
          filter: {},
        },
      }),
    select: (response) => response.data.departments?.sort((a, b) => a.name.localeCompare(b.name, 'ru')),
  })

  return {
    officesList,
    isOfficesLoading,
    positionsList,
    isPositionsLoading,
    departmentsList,
    isDepartmentsLoading,
  }
}

export const useDepartmentOptions = () => {
  const { data: departmentsList, isLoading: isDepartmentsLoading } = useQuery({
    queryKey: ['departments'],
    queryFn: () =>
      listDepartmentsPassportIdmIdmPrivateListDepartmentsPost({
        body: { filter: {} },
        throwOnError: true,
      }),
    select: (response) =>
      response.data.departments
        .map((item) => ({
          id: item.id,
          name: item.name,
        }))
        .sort((a, b) => a.name.localeCompare(b.name, 'ru')),
  })

  const { data: officesList, isLoading: isOfficesLoading } = useQuery({
    queryKey: ['offices'],
    queryFn: () =>
      listOfficesPassportIdmIdmPrivateListOfficesPost({
        body: { filter: {} },
        throwOnError: true,
      }),
    select: (response) => response.data.offices?.sort((a, b) => a.name.localeCompare(b.name, 'ru')),
  })

  return {
    departmentsList,
    isDepartmentsLoading,
    officesList,
    isOfficesLoading,
  }
}

export const useCityOptions = () => {
  const { data: countriesList, isLoading: isCountriesLoading } = useQuery({
    queryKey: ['countries'],
    queryFn: () =>
      listCountriesPassportIdmIdmPrivateListCountriesPost({
        body: { filter: {} },
        throwOnError: true,
      }),
    select: (response) => response.data.countries?.sort((a, b) => a.name.localeCompare(b.name, 'ru')),
  })

  return {
    countriesList,
    isCountriesLoading,
  }
}

export const useOfficeOptions = () => {
  const { data: citiesList, isLoading: isCitiesLoading } = useQuery({
    queryKey: ['cities'],
    queryFn: () =>
      listCitiesPassportIdmIdmPrivateListCitiesPost({
        body: { filter: {} },
        throwOnError: true,
      }),
    select: (response) => response.data.cities?.sort((a, b) => a.name.localeCompare(b.name, 'ru')),
  })

  return {
    citiesList,
    isCitiesLoading,
  }
}

interface FilterableOption {
  name: string
  [key: string]: unknown
}

export const createOptionsFilter = <T extends FilterableOption>(
  sourceList: Ref<T[] | undefined>,
  targetRef: Ref<T[] | undefined>,
) => {
  return (val: string, update: (cb: () => void) => void) => {
    update(() => {
      if (val === '') {
        targetRef.value = toValue(sourceList) || []
      } else {
        targetRef.value =
          toValue(sourceList)
            ?.filter((item) => item.name.toLowerCase().includes(val.toLowerCase()))
            .sort((a, b) => a.name.localeCompare(b.name, 'ru')) || []
      }
    })
  }
}

export const createTranslatableOptionsFilter = <T>(
  sourceList: T[],
  targetRef: Ref<T[]>,
  labelGetter: (item: T) => string,
) => {
  return (val: string, update: (cb: () => void) => void) => {
    update(() => {
      if (val === '') {
        targetRef.value = [...sourceList]
      } else {
        targetRef.value = sourceList.filter((item) => {
          return labelGetter(item).toLowerCase().includes(val.toLowerCase())
        })
      }
    })
  }
}
